import { matchPath, RouteProps, match } from "react-router-dom";
import { Actions as MasterSearchActions } from "@/store/masterSearch/slice";
import { Effects as MasterProfileEffects } from "@/store/masterProfile/effects";
import { Effects as ClientProfileEffects } from "@/store/clientProfile/effects";
import { Effects as SaloonProfileEffects } from "@/store/saloonProfile/effects";
import { Effects as ReviewEffects } from "@/store/reviews/effects";
import { Effects as PaymentResultEffects } from "@/store/paymentResult/effects";
import { Effects as PortfolioEffects } from "@/store/portfolio/effects";
import { Selectors as MasterProfileSelectors } from "@/store/masterProfile/selectors";
import { Selectors as ClientProfileSelectors } from "@/store/clientProfile/selectors";
import { Selectors as SaloonProfileSelectors } from "@/store/saloonProfile/selectors";
import { Selectors as BlogSelectors } from "@/store/blog/selectors";
import { Selectors as ReviewSelectors } from "@/store/reviews/selectors";
import { Selectors as PaymentResultSelectors } from "@/store/paymentResult/selectors";
import { Selectors as PortfolioSelectors } from "@/store/portfolio/selectors";
import { Selectors as UserSelectors } from "@/store/user/selectors";
import { Selectors as MsgrSubPageSelectors } from "@/store/msgrSubPage/selectors";
import { Effects as MsgrSubPageEffects } from "@/store/msgrSubPage/effects";
import { LinkGenerator } from "@/utils/LinkGenerator";
import { Effects as BlogEffects } from "@/store/blog/effects";
import { QueryKeys } from "./network/queryKeys";
import { IS_SERVER } from "./misc/constants";
import { SpecAPI } from "./api/specs";
import { MasterAPI } from "./api/master";
import type {
  ContentfulStatusCode,
  RedirectStatusCode,
} from "hono/utils/http-status";
import type { QueryClient } from "react-query";

type PrefetchOptions = {
  ctx: {
    store: any;
    queryClient: QueryClient;
    locale: string;
    query: Record<string, Maybe<string> | string[]>;
    throw: (status: ContentfulStatusCode, message: string) => any;
    redirect: (url: string, status?: RedirectStatusCode) => any;
  };
  matchedPath: match<any>;
};

interface PrefetchRoute extends RouteProps {
  prefetch: (options: PrefetchOptions) => Promise<void | Response>;
}

export const routes: PrefetchRoute[] = [
  {
    path: "/master",
    exact: true,
    prefetch: async ({ ctx }) => {
      let id = ctx.query.id;
      if (!id) {
        const state = ctx.store.getState();
        if (UserSelectors.isMaster(state)) {
          id = UserSelectors.getMasterId(state);
        } else {
          ctx.throw(400, "NOT_FOUND");
        }
      }
      await Promise.all([
        ctx.store.dispatch(MasterProfileEffects.loadProfile(id)),
        IS_SERVER &&
          ctx.queryClient.prefetchQuery([QueryKeys.similarMasters, id], () =>
            MasterAPI.getSimilarMasters(id),
          ),
      ]);
      const state = ctx.store.getState();
      const error = MasterProfileSelectors.getError(state);
      if (error) {
        ctx.throw(400, JSON.stringify(error));
      }
      const redirect = MasterProfileSelectors.getRedirectMeta(state);
      if (redirect.isRedirected) {
        return ctx.redirect(
          LinkGenerator.masterProfile(redirect.redirectId),
          301,
        );
      }
    },
  },
  {
    path: "/client",
    exact: true,
    prefetch: async ({ ctx }) => {
      let id = ctx.query.id;
      if (!id) {
        const userId = UserSelectors.getUserId(ctx.store.getState());
        if (userId) {
          id = userId;
        } else {
          ctx.throw(400, "NOT_FOUND");
        }
      }
      await ctx.store.dispatch(ClientProfileEffects.loadProfile(id));
      const state = ctx.store.getState();
      const error = ClientProfileSelectors.getError(state);
      if (error) {
        ctx.throw(400, JSON.stringify(error));
      }
      const redirect = ClientProfileSelectors.getRedirectMeta(state);
      if (redirect.isRedirected) {
        return ctx.redirect(
          LinkGenerator.clientProfile(redirect.redirectId),
          301,
        );
      }
    },
  },
  {
    path: "/salon",
    exact: true,
    prefetch: async ({ ctx }) => {
      const id = ctx.query.id;
      if (!id) ctx.throw(400, "NOT_FOUND");
      await Promise.all([
        ctx.store.dispatch(SaloonProfileEffects.loadProfile(id)),
        IS_SERVER &&
          ctx.queryClient.prefetchQuery(
            [QueryKeys.allSpecs, { locale: ctx.locale }],
            () => SpecAPI.getAll({ locale: ctx.locale }),
          ),
      ]);
      const error = SaloonProfileSelectors.getError(ctx.store.getState());
      if (error) {
        ctx.throw(400, JSON.stringify(error));
      }
    },
  },
  {
    path: "/blog/editor/:slug?",
    prefetch: async ({ matchedPath: params, ctx }) => {
      await ctx.store.dispatch(BlogEffects.initEditor(params.params.slug));
    },
  },
  {
    path: "/blog",
    exact: true,
    prefetch: async ({ ctx }) => {
      await Promise.all([
        ctx.store.dispatch(BlogEffects.loadArticles(ctx.query)),
        ctx.store.dispatch(BlogEffects.loadPopularArticles()),
        ctx.store.dispatch(BlogEffects.loadBestAuthors()),
      ]);
      const error = BlogSelectors.getArticlesError(ctx.store.getState());
      if (error) {
        ctx.throw(400, JSON.stringify(error));
      }
    },
  },
  {
    path: "/blog/article/:slug",
    prefetch: async ({ ctx, matchedPath: params }) => {
      await Promise.all([
        ctx.store.dispatch(BlogEffects.loadArticle(params.params.slug)),
        ctx.store.dispatch(BlogEffects.loadPopularArticles()),
        ctx.store.dispatch(BlogEffects.loadBestAuthors()),
      ]);
      const state = ctx.store.getState();
      const error = BlogSelectors.getArticleError(state);
      if (error) {
        ctx.throw(400, JSON.stringify(error));
      }
      const redirect = BlogSelectors.getArticleRedirectMeta(state);
      if (redirect.isRedirected) {
        return ctx.redirect(
          LinkGenerator.blogArticle(redirect.redirectId),
          301,
        );
      }
    },
  },
  {
    path: "/review/:id",
    prefetch: async ({ ctx, matchedPath: params }) => {
      const { id } = params.params;
      await ctx.store.dispatch(ReviewEffects.loadReview(id));
      const error = ReviewSelectors.getError(ctx.store.getState());
      if (error) {
        ctx.throw(400, JSON.stringify(error));
      }
    },
  },
  {
    path: "/portfolio/:id",
    prefetch: async ({ ctx, matchedPath: params }) => {
      const { id } = params.params;
      await ctx.store.dispatch(PortfolioEffects.loadSinglePortfolio(id));
      const error = PortfolioSelectors.getSingleError(ctx.store.getState());
      if (error) {
        ctx.throw(400, JSON.stringify(error));
      }
    },
  },
  {
    path: "/messenger/sub/:token",
    prefetch: async ({ ctx, matchedPath: params }) => {
      const { token } = params.params;
      await ctx.store.dispatch(MsgrSubPageEffects.loadData(token));
      const error = MsgrSubPageSelectors.getError(ctx.store.getState());
      if (error) {
        ctx.throw(400, JSON.stringify(error));
      }
    },
  },
  {
    path: "/thankyou",
    prefetch: async ({ ctx }) => {
      const query = ctx.query;
      const isSuccess =
        typeof query.Success === "string"
          ? query.Success === "true"
          : Boolean(query.Success);
      if (!query.OrderId) {
        ctx.throw(400, "TASK_NOT_FOUND");
      }
      await ctx.store.dispatch(
        PaymentResultEffects.loadTask(query.OrderId, isSuccess),
      );
      const state = ctx.store.getState();
      const error = PaymentResultSelectors.getError(state);
      if (error) {
        ctx.throw(400, JSON.stringify(error));
      }
    },
  },
  {
    path: "/search/:loc/:spec?",
    exact: true,
    prefetch: async ({ ctx, matchedPath }) => {
      // HACK: Casting to `any` to bypass a likely type error in the slice definition.
      // These actions are expected to accept a payload but are typed as taking none.
      // This should be corrected in the slice definition itself.
      ctx.store.dispatch(
        (MasterSearchActions.loadMasters as any)({
          query: ctx.query,
          params: matchedPath.params,
        }),
      );
      ctx.store.dispatch(
        (MasterSearchActions.loadGeo as any)({
          query: ctx.query,
          params: matchedPath.params,
        }),
      );
      return Promise.resolve();
    },
  },
];

export const matchRoute = (
  path: string,
): [PrefetchRoute, match<any>] | [null, null] => {
  for (let i = 0; i < routes.length; i++) {
    const matchedPath = matchPath(path, routes[i]);
    if (matchedPath) {
      return [routes[i], matchedPath];
    }
  }
  return [null, null];
};
